import { useState } from "react";
import { Star, Heart } from "lucide-react";
import { toast } from "sonner";

// Deterministic decorative rating (no review system yet) derived from the product id
function deriveRating(id) {
  const n = [...String(id)].reduce((a, c) => a + c.charCodeAt(0), 0);
  const rating = Math.min(5, 3.8 + (n % 13) / 10);
  const count = 3 + (n % 58);
  return { rating: Number(rating.toFixed(1)), count };
}

const WL_KEY = "ccc_wishlist";
const readWL = () => {
  try { return new Set(JSON.parse(localStorage.getItem(WL_KEY) || "[]")); } catch { return new Set(); }
};

export default function ProductCard({ product, onView }) {
  const { rating, count } = deriveRating(product.id);
  const [wished, setWished] = useState(() => readWL().has(product.id));

  const toggleWish = (e) => {
    e.stopPropagation();
    const wl = readWL();
    if (wl.has(product.id)) { wl.delete(product.id); setWished(false); toast("Removed from favorites"); }
    else { wl.add(product.id); setWished(true); toast.success("Saved to favorites"); }
    localStorage.setItem(WL_KEY, JSON.stringify([...wl]));
  };

  return (
    <div className="group relative flex flex-col" data-testid={`product-${product.id}`}>
      <div className="relative aspect-[4/5] overflow-hidden rounded-xl bg-navy border border-white/10 group-hover:border-electric/40 transition-colors duration-500">
        <button onClick={onView} className="absolute inset-0 w-full h-full text-left" aria-label={`View ${product.name}`}>
          <img src={product.image_url} alt={product.name}
            className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        </button>

        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5 items-start pointer-events-none">
          {product.seasonal && <span className="bg-electric/90 text-white text-[9px] font-bold uppercase tracking-[0.12em] px-2 py-1 rounded-full">Seasonal</span>}
          {product.new_drop && <span className="bg-royal text-white text-[9px] font-bold uppercase tracking-[0.12em] px-2 py-1 rounded-full">New</span>}
          {product.best_seller && <span className="bg-white text-navy-dark text-[9px] font-bold uppercase tracking-[0.12em] px-2 py-1 rounded-full">Best Seller</span>}
          {product.featured && !product.best_seller && <span className="bg-navy-dark/80 text-white text-[9px] font-bold uppercase tracking-[0.12em] px-2 py-1 rounded-full border border-white/20 backdrop-blur-sm">Featured</span>}
        </div>

        {/* Wishlist */}
        <button onClick={toggleWish} data-testid={`wishlist-${product.id}`}
          className={`absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center backdrop-blur-sm border transition-colors ${wished ? "bg-electric/20 border-electric text-electric" : "bg-navy-dark/40 border-white/15 text-white/70 hover:text-electric hover:border-electric"}`}
          aria-label="Add to favorites">
          <Heart size={15} className={wished ? "fill-electric" : ""} />
        </button>

        {/* Hover action */}
        <div className="absolute inset-x-0 bottom-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300 bg-navy-dark/85 backdrop-blur-md border-t border-white/10 p-3">
          <button onClick={onView} data-testid={`view-details-${product.id}`}
            className="w-full bg-electric text-white uppercase tracking-[0.18em] text-[11px] font-bold py-3 rounded-md hover:bg-royal transition-colors glow-blue-sm">
            View Details
          </button>
        </div>
      </div>

      {/* Meta */}
      <div className="pt-4 px-1">
        <div className="flex items-center gap-1 mb-1.5">
          {[0, 1, 2, 3, 4].map((i) => (
            <Star key={i} size={12} className={i < Math.round(rating) ? "fill-electric text-electric" : "text-chrome/25"} />
          ))}
          <span className="text-[11px] text-chrome/45 ml-1">{rating.toFixed(2)}({count})</span>
        </div>
        <button onClick={onView} className="block text-left text-chrome font-medium text-sm leading-tight hover:text-electric transition-colors line-clamp-1">
          {product.name}
        </button>
        <div className="mt-1.5 text-sm">
          <span className="text-chrome-bright font-semibold">From ${Number(product.price).toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
}
